import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { resetGapBeneficiary } from './reducer/gapBeneficiaryReducer';
import { useBackroute } from '../../../contexts/BackrouteContext';
import ExitModal from '../../Common/ExitModal';
import dayjs from 'dayjs';
import { getAge } from '../../../v2/Utils/AgeCalc/calculateAge';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';

import BenefitCard from '../../../v2/Benefits/Beneficiary/Components/BenefitCard';
import FirmDetails from '../../../v2/Benefits/Beneficiary/Components/FirmDetails';
import IconReusable from '../../../components/IconReusable';
import {
  TextField,
  CustomSelect,
  CustomCheckbox,
  CustomButton,
  DatePicker,
} from '../../../v2/MUI_custom_component';
import { toast } from 'react-toastify';
import { submitBeneficiaryNominee, getGapAllRelations } from './APIs/action';

const AddGapNominee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const insuranceData = location?.state?.insuranceData;
  const returnTransactionId = location?.state?.returnTransactionId;
  const { updateBackroute, resetBackroute } = useBackroute();
  const dispatch = useDispatch();

  const beneficiary_details = useSelector(
    (state) => state?.gapBeneficiaryReducer || {},
  );
  const { selectedEntity: company_esi_id } = useSelector(
    (state) => state.entityReducer,
  );
  const { is_PWA } = useSelector((state) => ({
    is_PWA: state.loginReducer.is_PWA,
  }));

  const [showExitModal, setShowExitModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const formatDobDisplay = (dob) => {
    if (!dob) return '-';
    const [yyyy, mm, dd] = dob.split('-');
    return new Date(`${yyyy}-${mm}-${dd}`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  useEffect(() => {
    updateBackroute({ customHandler: () => setShowExitModal(true) });
    return () => resetBackroute();
  }, []);

  const [nomineeForm, setNomineeForm] = useState({
    relation: '',
    relation_str_input: '',
    full_name: '',
    gender: '',
    dob: '',
    age: '',
  });
  const [maritalStatus, setMaritalStatus] = useState('');
  const [maritalStatusList, setMaritalStatusList] = useState([]);
  const [relationsByMaritalStatus, setRelationsByMaritalStatus] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const beneficiaryGender = beneficiary_details?.beneficiary?.gender || '';
  const normalizedGender = beneficiaryGender
    ? beneficiaryGender.charAt(0).toUpperCase() +
      beneficiaryGender.slice(1).toLowerCase()
    : '';
  const insuranceType = insuranceData?.benefit_card?.name;

  useEffect(() => {
    if (!normalizedGender || !insuranceType) return;
    let cancelled = false;
    (async () => {
      const res = await getGapAllRelations(normalizedGender, insuranceType);
      if (cancelled) return;
      if (res?.error) {
        toast.error(res?.message || 'Failed to fetch relations');
        return;
      }
      setMaritalStatusList(res?.data?.marital_status_list || []);
      setRelationsByMaritalStatus(res?.data?.relations || {});
    })();
    return () => {
      cancelled = true;
    };
  }, [normalizedGender, insuranceType]);

  const relationOptions = maritalStatus
    ? relationsByMaritalStatus[maritalStatus] || []
    : [];

  const handleFieldChange = (field, value) => {
    if (field === 'dob') {
      const birthDate = dayjs(value);
      const age = birthDate.isValid() ? dayjs().diff(birthDate, 'year') : '';
      setNomineeForm((prev) => ({
        ...prev,
        dob: value,
        age: age !== '' ? String(age) : '',
      }));
    } else {
      setNomineeForm((prev) => ({ ...prev, [field]: value }));
    }

    if (field === 'full_name') {
      const name = value.trim();
      if (name && !/^[a-zA-Z.\s]+$/.test(name)) {
        setErrors((prev) => ({
          ...prev,
          full_name: 'Only letters, spaces and dots are allowed',
        }));
      } else if (errors.full_name) {
        setErrors((prev) => {
          const { full_name: _, ...rest } = prev;
          return rest;
        });
      }
      return;
    }

    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!maritalStatus) newErrors.marital_status = 'Marital status is required';
    if (!nomineeForm.relation) newErrors.relation = 'Relation is required';
    if (nomineeForm.relation === 'Others') {
      const otherVal = nomineeForm.relation_str_input.trim();
      if (!otherVal) newErrors.relation_str_input = 'Please specify relation';
      else if (!/^[a-zA-Z.\s]+$/.test(otherVal))
        newErrors.relation_str_input =
          'Only letters, spaces and dots are allowed';
      else if (otherVal.length > 30)
        newErrors.relation_str_input = 'Maximum 30 characters allowed';
    }
    if (!nomineeForm.full_name.trim())
      newErrors.full_name = 'Full name is required';
    else if (!/^[a-zA-Z.\s]+$/.test(nomineeForm.full_name.trim()))
      newErrors.full_name = 'Only letters, spaces and dots are allowed';
    else if (nomineeForm.full_name.trim().length > 30)
      newErrors.full_name = 'Maximum 30 characters allowed';
    if (!nomineeForm.gender) newErrors.gender = 'Gender is required';
    if (!nomineeForm.dob) newErrors.dob = 'Date of birth is required';
    if (!confirmed) newErrors.confirmed = 'Please confirm to proceed';
    if (company_esi_id == '' && !is_PWA)
      toast.error('Company Esi Id cannot be empty');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const relationStr =
      nomineeForm.relation === 'Others'
        ? nomineeForm.relation_str_input.trim()
        : nomineeForm.relation;
    const payload = {
      version: 'v2',
      id_type: 1,
      insurance_id: insuranceData?.benefit_card?.name,
      session_id: returnTransactionId,
      firm_owner_name: beneficiary_details?.firm_owner_name,
      firm_type: beneficiary_details?.firm_type,
      beneficiary: {
        ...beneficiary_details?.beneficiary,
        gender: beneficiary_details?.beneficiary?.gender
          ? beneficiary_details.beneficiary.gender.toUpperCase()
          : beneficiary_details?.beneficiary?.gender,
        marital_status: maritalStatus,
      },
      nominee: {
        full_name: nomineeForm.full_name,
        gender: nomineeForm.gender ? nomineeForm.gender.toUpperCase() : nomineeForm.gender,
        dob: nomineeForm.dob,
        relation: nomineeForm.relation,
        relation_str: relationStr,
      },
      ...(!is_PWA && { company_esi_id: parseInt(company_esi_id, 10) }),
    };

    const { data, message, error } = await submitBeneficiaryNominee(payload);
    setSubmitting(false);
    if (!error) {
      toast.success(message || 'Gap beneficiary added successfully');
      navigate('/beneficiary/detail', {
        state: {
          insurance_type: insuranceData?.benefit_card?.name,
          selectedCompanyEntity: company_esi_id,
        },
      });
    } else {
      toast.error(message);
    }
  };

  const isSubmitDisabled =
    !maritalStatus ||
    !nomineeForm.relation ||
    (nomineeForm.relation === 'Others' &&
      !nomineeForm.relation_str_input.trim()) ||
    !nomineeForm.full_name.trim() ||
    !nomineeForm.gender ||
    !nomineeForm.dob ||
    !confirmed;

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, md: 2 },
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ alignItems: 'stretch', mb: 2 }}
      >
        <Grid
          item
          xs={12}
          sm={6}
        >
          <BenefitCard
            item={insuranceData?.benefit_card}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Card
            sx={{
              boxShadow: 'none',
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
            }}
          >
            {insuranceData?.selected_firm &&
              insuranceData.selected_firm.map((detail, i) => (
                <React.Fragment key={i}>
                  <FirmDetails detail={detail} />
                </React.Fragment>
              ))}
          </Card>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        sx={{ mb: 2 }}
      >
        {/* Beneficiary Details */}
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Box sx={{ background: '#fff', gap: 2, borderRadius: 2, p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                borderRadius: 2,
                background: '#fff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant='h2'>Beneficiary Details</Typography>
                <IconButton
                  sx={{ p: 0.5 }}
                  onClick={() => setShowDeleteModal(true)}
                >
                  <IconReusable
                    name='Delete'
                    color='#995A00'
                    size={16}
                  />
                </IconButton>
              </Box>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                {beneficiary_details?.existing_beneficiary?.details?.length > 0
                  ? [
                      {
                        display_name: 'Name',
                        display_value:
                          beneficiary_details.existing_beneficiary.header_title,
                      },
                      ...beneficiary_details.existing_beneficiary.details,
                    ].map(({ display_name, display_value }) => (
                      <Box
                        key={display_name}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          variant='labelD'
                          sx={{ color: '#666666' }}
                        >
                          {display_name}
                        </Typography>
                        <Typography
                          variant='p1'
                          sx={{ textAlign: 'right', color: '#333333' }}
                        >
                          {display_value || '—'}
                        </Typography>
                      </Box>
                    ))
                  : [
                      {
                        label: 'Name',
                        value: beneficiary_details?.beneficiary?.full_name,
                      },
                      {
                        label: 'Gender',
                        value: beneficiary_details?.beneficiary?.gender,
                      },
                      {
                        label: 'D.O.B',
                        value: formatDobDisplay(
                          beneficiary_details?.beneficiary?.dob,
                        ),
                      },
                      {
                        label: 'Age',
                        value: beneficiary_details?.beneficiary?.dob
                          ? getAge(beneficiary_details?.beneficiary?.dob)
                          : '—',
                      },
                      {
                        label: 'Mobile No.',
                        value: beneficiary_details?.beneficiary?.phone_number,
                      },
                      {
                        label: 'PAN',
                        value: beneficiary_details?.beneficiary?.pan,
                      },
                      {
                        label: 'Email ID',
                        value: beneficiary_details?.beneficiary?.email ? beneficiary_details?.beneficiary?.email : '-', 
                      },
                      {
                        label: 'Place of birth',
                        value: beneficiary_details?.beneficiary?.place_of_birth,
                      },
                      {
                        label: "Father's Name",
                        value:
                          beneficiary_details?.beneficiary
                            ?.manual_father_name,
                      },
                    ].map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          variant='labelD'
                          sx={{ color: '#666666' }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant='p1'
                          sx={{ textAlign: 'right', color: '#333333' }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Nominee Form */}
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {/* Beneficiary Marital Status */}
            <CustomSelect
              id='beneficiary-marital-status-select'
              label='Beneficiary marital status*'
              placeholder='Select Marital Status'
              value={maritalStatus}
              onChange={(e) => {
                setMaritalStatus(e.target.value);
                setNomineeForm((prev) => ({
                  ...prev,
                  relation: '',
                  relation_str_input: '',
                }));
                if (errors.marital_status) {
                  setErrors((prev) => {
                    const { marital_status: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              error={!!errors.marital_status}
              helperText={errors.marital_status}
              displayEmpty
              fullWidth
            >
              {maritalStatusList.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </CustomSelect>

            <Typography variant='h2'>Add Nominee Details</Typography>

            {/* Relation */}
            <CustomSelect
              id='nominee-relation-select'
              label='Relation*'
              placeholder='Select Relation'
              value={nomineeForm.relation}
              onChange={(e) => {
                handleFieldChange('relation', e.target.value);
                if (e.target.value !== 'Others') {
                  setNomineeForm((prev) => ({
                    ...prev,
                    relation_str_input: '',
                  }));
                }
              }}
              error={!!errors.relation}
              helperText={errors.relation}
              displayEmpty
              fullWidth
              disabled={!maritalStatus}
            >
              {relationOptions.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </CustomSelect>

            {nomineeForm.relation === 'Others' && (
              <TextField
                variant='standard'
                label='Others*'
                value={nomineeForm.relation_str_input}
                onChange={(e) =>
                  handleFieldChange('relation_str_input', e.target.value)
                }
                onBlur={() => {
                  const val = nomineeForm.relation_str_input.trim();
                  if (val && !/^[a-zA-Z.\s]+$/.test(val)) {
                    setErrors((prev) => ({
                      ...prev,
                      relation_str_input:
                        'Only letters, spaces and dots are allowed',
                    }));
                  }
                }}
                error={!!errors.relation_str_input}
                helperText={errors.relation_str_input}
                placeholder='Please specify relation'
                inputProps={{ maxLength: 30 }}
                fullWidth
              />
            )}

            {/* Full Name */}
            <TextField
              label='Full Name*'
              value={nomineeForm.full_name}
              onChange={(e) => handleFieldChange('full_name', e.target.value)}
              onBlur={() => {
                const name = nomineeForm.full_name.trim();
                if (name && !/^[a-zA-Z.\s]+$/.test(name)) {
                  setErrors((prev) => ({
                    ...prev,
                    full_name: 'Only letters, spaces and dots are allowed',
                  }));
                }
              }}
              error={!!errors.full_name}
              helperText={errors.full_name}
              placeholder='Enter Full Name'
              inputProps={{ maxLength: 30 }}
              fullWidth
            />

            {/* Gender */}
            <CustomSelect
              id='nominee-gender-select'
              label='Gender*'
              placeholder='Select Gender'
              value={nomineeForm.gender}
              onChange={(e) => handleFieldChange('gender', e.target.value)}
              error={!!errors.gender}
              helperText={errors.gender}
              displayEmpty
              fullWidth
            >
              <MenuItem value='MALE'>Male</MenuItem>
              <MenuItem value='FEMALE'>Female</MenuItem>
            </CustomSelect>

            {/* DOB + Age row */}
            <Box
              sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}
            >
              <Box sx={{ flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label='Date of Birth*'
                    value={nomineeForm.dob ? dayjs(nomineeForm.dob) : null}
                    onChange={(newValue) => {
                      const dateString = newValue
                        ? newValue.format('YYYY-MM-DD')
                        : '';
                      handleFieldChange('dob', dateString);
                    }}
                    error={!!errors.dob}
                    helperText={errors.dob}
                    disableFuture
                    format='DD/MM/YYYY'
                  />
                </LocalizationProvider>
              </Box>
              {nomineeForm?.age && (
                <Box
                  sx={{
                    width: '106px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <Typography
                    variant='labelD'
                    sx={{ color: '#666666' }}
                  >
                    Age
                  </Typography>
                  <Typography
                    variant='p1'
                    sx={{ fontSize: '16px !important' }}
                  >
                    {nomineeForm.age ? `${nomineeForm.age}y` : '—'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Consent checkbox */}
            {insuranceData?.confirmation_text?.show && (
              <Box>
                <CustomCheckbox
                  label={insuranceData.confirmation_text.label}
                  checked={confirmed}
                  onChange={(e) => {
                    setConfirmed(e.target.checked);
                    if (e.target.checked && errors.confirmed) {
                      setErrors((prev) => {
                        const { confirmed: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                />
                {errors.confirmed && (
                  <Typography
                    variant='caption'
                    color='error'
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {errors.confirmed}
                  </Typography>
                )}
              </Box>
            )}

            {/* Submit */}
            <CustomButton
              variant='filled'
              label={submitting ? 'Submitting...' : 'Submit'}
              onClick={handleSubmit}
              disabled={isSubmitDisabled || submitting}
              fullWidth
            />
          </Box>
        </Grid>
      </Grid>

      <ExitModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          dispatch(resetGapBeneficiary());
          navigate('/beneficiary/add-gap-beneficiary', {
            state: {
              insurance_type: insuranceData?.benefit_card?.name,
              selectedCompanyEntity: company_esi_id,
            },
          });
        }}
        title='Are you sure you want to delete?'
        subtitle='This action cannot be undone. All data would be lost?'
        confirmLabel='Yes'
        cancelLabel='No'
      />
      <ExitModal
        open={showExitModal}
        onClose={() => setShowExitModal(false)}
        onConfirm={() => navigate('/beneficiary')}
        title='Are you sure you want to exit?'
        subtitle='This action cannot be undone. All data would be lost?'
        confirmLabel='Yes'
        cancelLabel='No'
      />
    </Box>
  );
};

export default AddGapNominee;
